import { useContext, useEffect, useState } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import { EnterpriseContext } from "./EnterpriseContext";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import { confirmWrapper } from "../ui/askConfirm";
import truckyService from "../../common/services/trucky-service";

function SingleQuestion({ container, question, refreshQuestions }) {
  const [questionData, setQuestionData] = useState(question);
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const company = globalContext.company;

  if (!es.can(globalContext.member, "members.manage_applications")) {
    return;
  }

  const deleteQuestion = async (questionId) => {
    if (
      await confirmWrapper(container.localeManager.strings.areYouSure, {
        container: container,
      })
    ) {
      const result = await es.deleteCompanyQuestion(company.id, questionId);
      if (result.error) {
        toast.error(result.message);
      } else {
        refreshQuestions();
      }
    }
  };

  const updateQuestion = async (question) => {
    const result = await es.updateCompanyQuestion(company.id, question);
    if (result.error) {
      toast.error(result.message);
    } else {
      refreshQuestions();
      toast.success(container.localeManager.strings.dataUpdated);
    }
  };

  return (
    <Card className="mb-3 bg-light">
      <CardBody>
        <FormGroup row>
          <Col sm={8}>
            <Label>{container.localeManager.strings.question}</Label>
            <Input
              type="textarea"
              value={questionData.question}
              onChange={(e) => {
                questionData.question = e.target.value;
                setQuestionData({ ...questionData });
              }}
            />
          </Col>
          <Col sm={2}>
            <Label>{container.localeManager.strings.questionType}</Label>
            <Input
              type="select"
              value={questionData.type}
              onChange={(e) => {
                questionData.type = e.target.value;
                setQuestionData({ ...questionData });
              }}
            >
              <option value="text">Short Text</option>
              <option value="textarea">Long Text</option>
              <option value="select">Dropdown</option>
              <option value="checkbox">Checkbox</option>
              <option value="radio">Radio</option>
            </Input>
          </Col>
          <Col sm={2}>
            <Label>{container.localeManager.strings.required}</Label>
            <div>
              <input
                onChange={(e) =>
                  setQuestionData({
                    ...questionData,
                    is_required: e.target.checked,
                  })
                }
                class="form-check-input"
                type="checkbox"
              />
            </div>
          </Col>
        </FormGroup>
        {(questionData.type == "select" || questionData.type == "radio") && (
          <FormGroup row>
            <Col sm={12}>
              <Label>{container.localeManager.strings.questionOptions}</Label>
              <Input
                type="textarea"
                value={questionData.options || ""}
                onChange={(e) => {
                  questionData.options = e.target.value;
                  setQuestionData({ ...questionData });
                }}
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Col sm={2}>
            <Label>{container.localeManager.strings.order}</Label>
            <Input
              type="number"
              min="0"
              onChange={(e) =>
                setQuestionData({
                  ...questionData,
                  order: e.target.value,
                })
              }
            />
          </Col>
        </FormGroup>
        <div className="d-flex justify-content-end">
          <Button
            color="primary"
            size="sm"
            onClick={() => updateQuestion(questionData)}
          >
            <FontAwesomeIcon icon="check" />{" "}
            {container.localeManager.strings.save}
          </Button>
          <Button
            color="danger"
            size="sm"
            className="ms-2"
            onClick={() => deleteQuestion(questionData.id)}
          >
            <FontAwesomeIcon icon="trash" />{" "}
            {container.localeManager.strings.delete}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function ApplicationQuestionnaireManagement({ container }) {
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const [questions, setQuestions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const company = globalContext.company;
  const [newQuestion, setNewQuestion] = useState({
    question: null,
    type: "text",
    options: null, // comma separated options for radio/checkbox questions
  });

  useEffect(() => {
    truckyService.setDocumentTitle("Questionnaire");
  }, []);

  useEffect(() => {
    getQuestions();
  }, []);

  const getQuestions = async () => {
    setLoadingData(true);
    const result = await es.getCompanyQuestions(company.id);
    if (result.error) {
      toast.error(result.message);
    } else {
      setQuestions(result);
    }
    setLoadingData(false);
  };

  const addQuestion = async () => {
    setLoadingData(true);
    const result = await es.createCompanyQuestion(company.id, newQuestion);
    if (result.error) {
      toast.error(result.message);
    } else {
      getQuestions();
    }
    setLoadingData(false);
    setNewQuestion({
      question: null,
      type: "text",
      options: null,
    });
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>

        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/company/members",
                        title: container.localeManager.strings.members,
                      },
                      {
                        to: "/applications",
                        title: container.localeManager.strings.applications,
                      },
                      {
                        title: container.localeManager.strings.questionnaire,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="d-flex justify-content-between">
                    <h5>{container.localeManager.strings.questionnaire}</h5>
                  </div>
                  <FormGroup row>
                    <Col>
                      <Label>{container.localeManager.strings.question}</Label>
                      <Input
                        type="textarea"
                        onChange={(e) =>
                          setNewQuestion({
                            ...newQuestion,
                            question: e.target.value,
                          })
                        }
                        value={newQuestion.question || ""}
                      />
                    </Col>
                    <Col>
                      <Label>
                        {container.localeManager.strings.questionType}
                      </Label>
                      <Input
                        type="select"
                        onChange={(e) =>
                          setNewQuestion({
                            ...newQuestion,
                            type: e.target.value,
                          })
                        }
                        value={newQuestion.type || ""}
                      >
                        <option value="">Select Type</option>
                        <option value="text">Short Text</option>
                        <option value="textarea">Long Text</option>
                        <option value="select">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="radio">Radio</option>
                      </Input>
                    </Col>
                    <Col sm={2}>
                      <Label>{container.localeManager.strings.required}</Label>
                      <div>
                        <input
                          onChange={(e) =>
                            setNewQuestion({
                              ...newQuestion,
                              is_required: e.target.checked,
                            })
                          }
                          class="form-check-input"
                          type="checkbox"
                        />
                      </div>
                    </Col>
                  </FormGroup>
                  {(newQuestion.type == "select" ||
                    newQuestion.type == "radio") && (
                    <FormGroup row>
                      <Col sm={6}>
                        <Label>
                          {container.localeManager.strings.questionOptions}
                        </Label>
                        <Input
                          type="textarea"
                          onChange={(e) =>
                            setNewQuestion({
                              ...newQuestion,
                              options: e.target.value,
                            })
                          }
                          value={newQuestion.options || ""}
                        />
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col sm={2}>
                      <Label>{container.localeManager.strings.order}</Label>
                      <Input
                        type="number"
                        min="0"
                        onChange={(e) =>
                          setNewQuestion({
                            ...newQuestion,
                            order: e.target.value,
                          })
                        }
                        value={newQuestion.order || ""}
                      />
                    </Col>
                  </FormGroup>
                  <div>
                    <Button
                      color="primary"
                      onClick={addQuestion}
                      disabled={loadingData}
                      size="sm"
                    >
                      {container.localeManager.strings.addQuestion}
                    </Button>
                  </div>
                </CardBody>
                {questions.map((question) => (
                  <SingleQuestion
                    key={question.id}
                    container={container}
                    question={question}
                    refreshQuestions={getQuestions}
                  />
                ))}
              </Card>
            </Col>
          </Row>
        </Col>
        <aside className="ad-column-placeholder">
          <BoxDynamicAd container={container} className="my-3" />
        </aside>
      </Row>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
}
